import React, { ChangeEvent, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, InputAdornment, Menu, MenuItem, TextField, Typography } from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded';

const INPUT_HEIGHT = 40;
const BORDER_RADIUS = '12px';
interface ClientsFilterBarProps {
  searchQuery: string;
  onSearchChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onSearchClear?: () => void;
  searchInputRef?: React.Ref<HTMLInputElement>;
  locations?: string[];
  selectedLocation?: string;
  onLocationSelect?: (location: string) => void;
  onExport?: () => void;
  showFilterButton?: boolean;
  showExportButton?: boolean;
  enableSearchShortcut?: boolean;
  viewMode?: 'grid' | 'list';
  onViewModeChange?: (mode: 'grid' | 'list') => void;
}

export const ClientsFilterBar: React.FC<ClientsFilterBarProps> = ({
  searchQuery,
  onSearchChange,
  onSearchClear,
  searchInputRef,
  locations = [],
  selectedLocation = '',
  onLocationSelect = () => undefined,
  onExport,
  showFilterButton = true,
  showExportButton = true,
  enableSearchShortcut = true,
  viewMode = 'grid',
  onViewModeChange,
}) => {
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const isFilterMenuOpen = Boolean(filterAnchorEl);
  const localSearchInputRef = useRef<HTMLInputElement | null>(null);
  const sortedLocations = useMemo(
    () => [...locations].filter(Boolean).sort((a, b) => a.localeCompare(b)),
    [locations]
  );

  const setMergedSearchInputRef = useCallback((node: HTMLInputElement | null) => {
    localSearchInputRef.current = node;

    if (!searchInputRef) {
      return;
    }

    if (typeof searchInputRef === 'function') {
      searchInputRef(node);
      return;
    }

    (searchInputRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
  }, [searchInputRef]);

  useEffect(() => {
    if (!enableSearchShortcut) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '/' && event.key !== '?') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.defaultPrevented) return;

      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      const isEditable =
        tagName === 'input' ||
        tagName === 'textarea' ||
        tagName === 'select' ||
        !!target?.isContentEditable;

      if (isEditable) return;
      if (!localSearchInputRef.current) return;

      event.preventDefault();
      localSearchInputRef.current.focus();
    };

    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [enableSearchShortcut]);

  const handleOpenFilterMenu = (event: MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilterMenu = () => {
    setFilterAnchorEl(null);
  };

  const handleSelectLocation = (location: string) => {
    onLocationSelect(location);
    handleCloseFilterMenu();
  };

  const hasActiveLocationFilter = showFilterButton && Boolean(selectedLocation);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: { xs: 'flex-start', md: 'flex-end' },
        gap: { xs: 0.75, sm: 1.25 },
        flexShrink: 0,
        minWidth: 0,
        width: { xs: '100%', md: 'auto' },
      }}
    >
      <TextField
        placeholder="Search"
        value={searchQuery}
        onChange={onSearchChange}
        onKeyDown={(event) => {
          if (event.key !== 'Escape' || searchQuery.length === 0) return;
          event.preventDefault();
          event.stopPropagation();
          onSearchClear?.();
        }}
        inputRef={setMergedSearchInputRef}
        size="small"
        sx={{
          flex: { xs: '1 1 auto', sm: '0 0 auto' },
          width: { xs: 'auto', sm: 280 },
          minWidth: { xs: 0, sm: 220 },
          maxWidth: { xs: '100%', sm: 280 },
          '& .MuiOutlinedInput-root': {
            height: { xs: 32, sm: 34 },
            borderRadius: '999px',
            bgcolor: '#f6f7f9',
            pl: { xs: 0.25, sm: 0.5 },
            pr: { xs: 0.375, sm: 0.5 },
            '& fieldset': { borderColor: '#eaecf0' },
            '&:hover fieldset': { borderColor: '#dde1e7' },
            '&.Mui-focused fieldset': { borderColor: '#d5dae2' },
          },
          '& .MuiInputBase-input': {
            fontFamily: 'Inter, sans-serif',
            fontSize: { xs: '12px', sm: '13px' },
            color: '#101828',
            py: 0,
          },
          '& .MuiInputBase-input::placeholder': {
            color: '#a8acb3',
            opacity: 1,
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRoundedIcon sx={{ color: '#b0b3b8', fontSize: { xs: 18, sm: 20 } }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment
              position="end"
              sx={{ display: { xs: 'none', sm: 'flex' } }}
            >
              <Box
                component="span"
                sx={{
                  width: { xs: 20, sm: 24 },
                  height: { xs: 18, sm: 22 },
                  borderRadius: { xs: '7px', sm: '8px' },
                  border: '1px solid #d0d2d6',
                  bgcolor: '#fff',
                  color: '#b4b6bb',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: { xs: '12px', sm: '14px' },
                  lineHeight: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                /
              </Box>
            </InputAdornment>
          ),
        }}
      />
      {showFilterButton && (
        <>
          <IconButton
            aria-label="Filters"
            onClick={handleOpenFilterMenu}
            sx={{
              p: 0.5,
              flexShrink: 0,
              border: 'none',
              borderRadius: 0,
              bgcolor: 'transparent',
              color: hasActiveLocationFilter ? '#0f6f96' : '#475467',
              '&:hover': {
                bgcolor: 'transparent',
                color: hasActiveLocationFilter ? '#0f6f96' : '#344054',
              },
            }}
          >
            <TuneRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <Menu
            anchorEl={filterAnchorEl}
            open={isFilterMenuOpen}
            onClose={handleCloseFilterMenu}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: '12px',
                border: '1px solid #e4e7ec',
                boxShadow: '0 10px 30px rgba(16, 24, 40, 0.12)',
                minWidth: 220,
              },
            }}
          >
            <MenuItem onClick={() => handleSelectLocation('')}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.875rem' }}>All Locations</Typography>
                {!hasActiveLocationFilter && <CheckRoundedIcon sx={{ fontSize: 18, color: '#25a8da' }} />}
              </Box>
            </MenuItem>

            {sortedLocations.length === 0 ? (
              <MenuItem disabled>
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', color: '#98a2b3' }}>
                  No locations available
                </Typography>
              </MenuItem>
            ) : (
              sortedLocations.map((location) => (
                <MenuItem key={location} onClick={() => handleSelectLocation(location)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.875rem',
                        color: '#344054',
                      }}
                    >
                      {location}
                    </Typography>
                    {selectedLocation === location && <CheckRoundedIcon sx={{ fontSize: 18, color: '#25a8da' }} />}
                  </Box>
                </MenuItem>
              ))
            )}
          </Menu>
        </>
      )}
      {showExportButton && onExport && (
        <>
          {onViewModeChange && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35, flexShrink: 0 }}>
              <IconButton
                aria-label="Grid view"
                onClick={() => onViewModeChange('grid')}
                sx={{
                  p: 0.5,
                  color: viewMode === 'grid' ? '#20AEDC' : '#64748b',
                  bgcolor: viewMode === 'grid' ? 'rgba(32, 174, 220, 0.14)' : 'transparent',
                }}
              >
                <GridViewRoundedIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton
                aria-label="List view"
                onClick={() => onViewModeChange('list')}
                sx={{
                  p: 0.5,
                  color: viewMode === 'list' ? '#20AEDC' : '#64748b',
                  bgcolor: viewMode === 'list' ? 'rgba(32, 174, 220, 0.14)' : 'transparent',
                }}
              >
                <ViewListRoundedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          )}
          <Box sx={{ display: { xs: 'none', sm: 'inline-flex' }, flexShrink: 0 }}>
            <Button
              onClick={onExport}
              endIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: '0.8125rem',
                textTransform: 'none',
                whiteSpace: 'nowrap',
                height: 34,
                px: 1.6,
                borderRadius: BORDER_RADIUS,
                border: '1px solid #d0d5dd',
                bgcolor: '#fff',
                color: '#344054',
                '&:hover': {
                  bgcolor: '#f8fafc',
                  borderColor: '#b8c4d1',
                },
              }}
            >
              Export
            </Button>
          </Box>
          <IconButton
            onClick={onExport}
            aria-label="Export"
            sx={{
              display: { xs: 'inline-flex', sm: 'none' },
              flexShrink: 0,
              width: 32,
              height: 32,
              borderRadius: BORDER_RADIUS,
              border: '1px solid #d0d5dd',
              bgcolor: '#fff',
              color: '#344054',
              '&:hover': {
                bgcolor: '#f8fafc',
                borderColor: '#b8c4d1',
              },
            }}
          >
            <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </>
      )}
    </Box>
  );
};
